import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import { api, s3Api } from "./configs/axiosConfigs";
import { defineCancelApiObject } from "./configs/axiosUtils";
import { UploadQueue } from "../utils/UploadQueue";

export const CsrAPI = {
  _uploadQueue: new UploadQueue(),
  getAllStudies: async function (cancel = false) {
    const response = await api.request({
      url: "/dashboard-documents",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getAllStudies.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data.documents;
  },
  createStudy: async function ({ studyName, studyType }, cancel = false) {
    const response = await api.request({
      url: "/studies",
      method: "POST",
      params: { study_name: studyName, study_type: studyType },
      signal: cancel
        ? cancelApiObject[this.createStudy.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data.attribute_values;
  },
  createSafetySummary: async function ({ summaryName }, cancel = false) {
    const response = await api.request({
      url: "/clinical_summaries/safety_summary",
      method: "POST",
      data: { summaryName, summaryType: "SCS" },
      signal: cancel
        ? cancelApiObject[
            this.createSafetySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummary: async function ({ summaryId }, cancel = false) {
    const response = await api.request({
      url: "/clinical_summaries/safety_summary/" + summaryId,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getOneStudy: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getOneStudy.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data.attribute_values;
  },
  // Wizard data is stored per page. `section` is one of the values in
  // WIZARD_SECTIONS (study_design, efficacy, safety, standard_pk,
  // comparative_pk, bioavailability, standard_pd, comparative_pd,
  // data_mapping).
  getWizardSection: async function ({ studyId, section }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/wizards/" + section,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getWizardSection.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    // An unanswered section comes back as {} rather than a 404.
    // return response.data?.sectionData ?? {};
    return response.data ?? {};
  },
  saveWizardSection: async function (
    { studyId, section, sectionData },
    cancel = false
  ) {
    const response = await api.request({
      url: "/studies/" + studyId + "/wizards/" + section,
      method: "PATCH",
      data: sectionData,
      signal: cancel
        ? cancelApiObject[
            this.saveWizardSection.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    // return response.data?.sectionData ?? {};
    return response.data ?? {};
  },
  async uploadDocument({ studyId, file, docType }, onUploadProgress) {
    if (isProduction) {
      Sentry.setContext("file", {
        name: file.name,
        size: file.size,
      });
    }

    const uploadFn = async uploadState => {
      const initResponse = await this._initUpload(studyId, file, docType);
      const { presigned_url, document_id, required_headers } =
        initResponse.data;

      uploadState.documentId = document_id;

      await this._uploadToPresignedUrl(
        file,
        presigned_url,
        onUploadProgress,
        () => this._initUpload(studyId, file, docType),
        required_headers
      );

      return { success: true };
    };

    const deleteDocumentFn = async uploadState => {
      await this.deleteDocument({
        studyId,
        documentId: uploadState.documentId,
      });
    };

    return this._uploadQueue.add({
      file,
      uploadFn,
      deleteDocumentFn,
    });
  },

  async _initUpload(studyId, file, docType) {
    return api.request({
      url: `/studies/${studyId}/upload/init`,
      method: "POST",
      params: { document_type: docType },
      data: {
        filename: file.name,
        fileSize: file.size,
      },
    });
  },

  async _uploadToPresignedUrl(
    file,
    presignedUrl,
    onUploadProgress,
    retryInit,
    requiredHeaders
  ) {
    const put = (url, extraHeaders) =>
      s3Api.put(url, file, {
        headers: {
          "Content-Type": undefined,
          Authorization: undefined,
          ...(extraHeaders || {}),
        },
        onUploadProgress,
      });

    try {
      await put(presignedUrl, requiredHeaders);
    } catch (error) {
      // If 403, the presigned URL has expired — get a new one and retry once
      if (error.response?.status === 403 && retryInit) {
        const retryResponse = await retryInit();
        await put(
          retryResponse.data.presigned_url,
          retryResponse.data.required_headers
        );
      } else {
        throw error;
      }
    }
  },
  getDocuments: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/documents",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getDocuments.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    const documents = response.data.documents
      .map(obj => Object.values(obj))
      .flat();
    return documents;
  },
  async uploadSafetySummaryDocument(
    { summaryId, file, docType },
    onUploadProgress
  ) {
    if (isProduction) {
      Sentry.setContext("file", {
        name: file.name,
        size: file.size,
      });
    }

    const uploadFn = async uploadState => {
      const initResponse = await this._initSafetySummaryUpload(
        summaryId,
        file,
        docType
      );
      const { presignedUrl, requiredHeaders } = initResponse.data;

      uploadState.fileName = file.name;

      await this._uploadToPresignedUrl(
        file,
        presignedUrl,
        onUploadProgress,
        () => this._initSafetySummaryUpload(summaryId, file, docType),
        requiredHeaders
      );

      return { success: true };
    };

    const deleteDocumentFn = async uploadState => {
      if (uploadState.fileName) {
        await this.deleteSafetySummaryDocument({
          summaryId,
          fileName: uploadState.fileName,
        });
      }
    };

    return this._uploadQueue.add({
      file,
      uploadFn,
      deleteDocumentFn,
    });
  },

  async _initSafetySummaryUpload(summaryId, file, docType) {
    return api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/files/upload/init`,
      method: "POST",
      data: {
        filename: file.name,
        fileSize: file.size,
        documentType: docType,
      },
    });
  },

  getSafetySummaryDocuments: async function ({ summaryId }, cancel = false) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/files`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryDocuments.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    const data = response.data;
    let rawDocs = [];
    if (Array.isArray(data)) rawDocs = data;
    else if (Array.isArray(data?.files)) rawDocs = data.files;
    else if (Array.isArray(data?.documents)) {
      rawDocs = data.documents.map(obj => Object.values(obj)).flat();
    }
    return rawDocs.map(doc => ({
      ...doc,
      filename: doc.filename ?? doc.fileName,
      document_type: doc.document_type ?? doc.documentType,
    }));
  },
  getSafetySummaryTflContent: async function ({ summaryId }, cancel = false) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/tfl_content`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryTflContent.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummaryQuestions: async function (cancel = false) {
    const response = await api.request({
      url: "/clinical_summaries/safety_summary/questions",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryQuestions.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummaryDescriptionOfStudies: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/description_of_studies`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryDescriptionOfStudies.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data.sectionData;
  },
  saveSafetySummaryDescriptionOfStudies: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/description_of_studies`,
      method: "PATCH",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveSafetySummaryDescriptionOfStudies.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummarySubjectDisposition: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/subject_disposition`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummarySubjectDisposition.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data.sectionData;
  },
  saveSafetySummarySubjectDisposition: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/subject_disposition`,
      method: "PATCH",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveSafetySummarySubjectDisposition.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummaryOverallExtentOfExposure: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/extent_of_exposure`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryOverallExtentOfExposure.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    // The saved answers come back under `sectionData`; tolerate them being
    // returned bare so the page still re-populates either way.
    return response.data?.sectionData ?? response.data;
  },
  saveSafetySummaryOverallExtentOfExposure: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/extent_of_exposure`,
      method: "PATCH",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveSafetySummaryOverallExtentOfExposure.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummaryAnalysisOfAdverseEvents: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/aes`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryAnalysisOfAdverseEvents.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    // The saved answers come back under `sectionData`; tolerate them being
    // returned bare so the page still re-populates either way.
    return response.data?.sectionData ?? response.data;
  },
  saveSafetySummaryAnalysisOfAdverseEvents: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/aes`,
      method: "PATCH",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveSafetySummaryAnalysisOfAdverseEvents.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummaryClinicalLaboratoryEvaluations: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/labs_and_vital_signs`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryClinicalLaboratoryEvaluations.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    // The saved answers come back under `sectionData`; tolerate them being
    // returned bare so the page still re-populates either way.
    return response.data?.sectionData ?? response.data;
  },
  saveSafetySummaryClinicalLaboratoryEvaluations: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/labs_and_vital_signs`,
      method: "PATCH",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveSafetySummaryClinicalLaboratoryEvaluations.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummarySafetyInSpecialGroups: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/special_groups`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummarySafetyInSpecialGroups.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    // The saved answers come back under `sectionData`; tolerate them being
    // returned bare so the page still re-populates either way.
    return response.data?.sectionData ?? response.data;
  },
  saveSafetySummarySafetyInSpecialGroups: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/special_groups`,
      method: "PATCH",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveSafetySummarySafetyInSpecialGroups.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getSafetySummaryPostMarketingData: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/post_marketing`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryPostMarketingData.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data.sectionData;
  },
  saveSafetySummaryPostMarketingData: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/post_marketing`,
      method: "PATCH",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveSafetySummaryPostMarketingData.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  // Queues the (async, SQS-backed) TriloDocs mapping job for a safety summary.
  // A 202 means the job was accepted and the caller should start polling
  // getSafetySummaryMapping; the status is returned alongside the body because
  // axios treats every 2xx as a success. Surfaces the backend's structured
  // error body ({ error, message }) on 4xx/5xx so the caller can show it.
  submitSafetySummaryMapping: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/mapping`,
      method: "POST",
      data: state ?? {},
      signal: cancel
        ? cancelApiObject[
            this.submitSafetySummaryMapping.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return { status: response.status, data: response.data };
  },
  // Retrieves the TriloDocs-proposed mapping, or the user's saved edits to it.
  // 200 carries the mapping payload; 202 means the background job is still
  // crunching and the caller should poll again. Both resolve, so the status is
  // returned with the body for the caller to branch on.
  getSafetySummaryMapping: async function ({ summaryId }, cancel = false) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/mapping`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryMapping.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return { status: response.status, data: response.data };
  },
  // Persists the user's Data Mapping wizard selections (the dropdown state) so
  // they are restored on a later visit to the page.
  saveSafetySummaryDataMapping: async function (
    { summaryId, state },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/wizards/data_mapping`,
      method: "PATCH",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveSafetySummaryDataMapping.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  deleteSafetySummaryDocument: async function (
    { summaryId, fileName },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/files/${encodeURIComponent(fileName)}`,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[
            this.deleteSafetySummaryDocument.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  deleteAllSafetySummaryDocuments: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/files`,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[
            this.deleteAllSafetySummaryDocuments.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  reloadSafetySummaryDocuments: async function ({ summaryId }, cancel = false) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/files/reload`,
      method: "POST",
      signal: cancel
        ? cancelApiObject[
            this.reloadSafetySummaryDocuments.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  // ---- SCS render: generate, poll, download, version history ----
  // Queues a background render and answers 202 with the requestId of this run;
  // that id is what the status and version endpoints are keyed by.
  renderSafetySummary: async function ({ summaryId }, cancel = false) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/render`,
      method: "POST",
      signal: cancel
        ? cancelApiObject[
            this.renderSafetySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return { httpStatus: response.status, ...response.data };
  },
  // 202 while the latest render is still running, 200 once it has finished (the
  // body then also carries the trafficLights report). The HTTP code is the
  // authoritative done/not-done signal, so hand it back with the payload.
  getSafetySummaryRenderStatus: async function ({ summaryId }, cancel = false) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/render/status`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryRenderStatus.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return { httpStatus: response.status, ...response.data };
  },
  // 200 hands back a presigned link to the latest finished .docx, 202 means the
  // render is still in flight — again the caller needs the HTTP code.
  getSafetySummaryDownloadUrl: async function ({ summaryId }, cancel = false) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/render/download_url`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryDownloadUrl.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return { httpStatus: response.status, ...response.data };
  },
  // Every finished render on the summary, as a flat requestId -> ISO timestamp
  // map. Presentation (ordering, labels) is left to the caller.
  getSafetySummaryRenderVersions: async function (
    { summaryId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/render/versions`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryRenderVersions.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  // Presigned links for one earlier render: documentUrl (.docx) and reportUrl
  // (.xlsx traffic-light report).
  getSafetySummaryRenderVersion: async function (
    { summaryId, requestId },
    cancel = false
  ) {
    const response = await api.request({
      url: `/clinical_summaries/safety_summary/${summaryId}/render/versions/${requestId}`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getSafetySummaryRenderVersion.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  // Fetches a presigned link's contents as a blob. Kept separate from the
  // link-producing calls so a caller holding several links (a version's .docx
  // and .xlsx) can download each with its own request.
  downloadPresignedFile: async function ({ url }, cancel = false) {
    const response = await s3Api.get(url, {
      responseType: "blob",
      signal: cancel
        ? cancelApiObject[
            this.downloadPresignedFile.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  deleteDocument: async function ({ studyId, documentId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/documents/" + documentId,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[this.deleteDocument.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  requestCsr: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/csr/request",
      method: "POST",
      signal: cancel
        ? cancelApiObject[this.requestCsr.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  abortCsr: async function ({ studyId, requestId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/csr/" + requestId,
      method: "POST",
      signal: cancel
        ? cancelApiObject[this.abortCsr.name].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  getCsrStatus: async function ({ studyId, requestId }, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId + "/csr/" + requestId + "/status",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getCsrStatus.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  downloadLatestFile: async function ({ studyId, fileURL }, cancel = false) {
    try {
      const presignedUrlResponse = await api.request({
        url: fileURL,
        method: "GET",
        signal: cancel
          ? cancelApiObject[
              this.downloadLatestFile.name
            ].handleRequestCancellation().signal
          : undefined,
      });
      const data = presignedUrlResponse.data;
      let url;
      if (data !== null && typeof data === "object" && !Array.isArray(data)) {
        url = presignedUrlResponse.data.url;
      } else {
        url = presignedUrlResponse.data[0].url;
      }

      if (isProduction) {
        Sentry.setContext("presignedUrl", {
          url: url,
          studyId: studyId,
          timestamp: new Date().toISOString(),
        });
      }

      try {
        const response = await s3Api.get(url, {
          responseType: "blob",
          signal: cancel
            ? cancelApiObject[
                this.downloadLatestFile.name
              ].handleRequestCancellation().signal
            : undefined,
        });

        if (isProduction) {
          Sentry.setContext("downloadResponse", {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(Object.entries(response.headers)),
            size: response.data.size,
            type: response.data.type,
          });
        }

        return response.data;
      } catch (presignedError) {
        const error = new Error("Presigned URL request failed");
        error.originalError = presignedError;
        error.isPresignedUrlError = true;
        error.status = presignedError.response?.status;
        if (isProduction) {
          Sentry.captureException(error);
        }
        throw error;
      }
    } catch (error) {
      if (isProduction) {
        Sentry.captureException(error);
      }
      if (!error.isPresignedUrlError) {
        throw error;
      }
      const newError = new Error("Failed to download CSR");
      newError.originalError = error;
      throw newError;
    }
  },
  deleteStudy: async function (studyId, cancel = false) {
    const response = await api.request({
      url: "/studies/" + studyId,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[this.deleteStudy.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response;
  },
  getFeatureFlags: async function () {
    const response = await api.request({
      url: "/features",
      method: "GET",
    });
    return response;
  },
  // RBAC map for the logged-in user, e.g. { "csr:dashboard:create": true }.
  getPermissions: async function () {
    const response = await api.request({
      url: "/me/permissions",
      method: "GET",
    });
    console.log("Permissions response:", response.data);
    return response.data;
    // return {
    //   "csr:dashboard:read": true,
    //   "csr:dashboard:create": true,
    //   "csr:dashboard:delete": true,
    //   "csr:files:read": true,
    //   "csr:files:create": true,
    //   "csr:files:update": true,
    //   "csr:files:delete": true,
    //   "csr:wizards:read": true,
    //   "csr:wizards:update": true,
    //   "csr:generation:create": true,
    //   "csr:generation:update": true,
    //   "csr:downloads:read": true,
    //   "ls:dashboard:read": true,
    //   "ls:dashboard:create": true,
    //   "ls:dashboard:delete": true,
    //   "ls:wizards:read": true,
    //   "ls:wizards:update": true,
    //   "ls:generation:create": true,
    //   "ls:generation:read": true,
    //   "scs:dashboard:create": true,
    //   "scs:dashboard:read": false,
    //   "scs:dashboard:delete": false,
    //   "scs:files:create": true,
    //   "scs:files:read": false,
    //   "scs:files:update": false,
    //   "scs:files:delete": false,
    //   "scs:wizards:read": true,
    //   "scs:wizards:update": true,
    //   "scs:generation:create": true,
    //   "scs:downloads:read": true,
    // };
    //TODO role should be displayed
  },
  login: async function () {
    const response = await api.request({
      url: "/login",
      method: "POST",
    });
    return response;
  },
  logout: async function () {
    const response = await api.request({
      url: "/logout",
      method: "POST",
    });
    return response;
  },
  getToTFLStatus: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/to-tfl/status`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getToTFLStatus.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  getAllStatuses: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/status`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getAllStatuses.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  getTableNames: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/tfl-content?content_type=tables`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getTableNames.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  getFigureNames: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/tfl-content?content_type=figures`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getFigureNames.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  getTrafficLightReports: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/reports`,
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getTrafficLightReports.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  downloadTrafficLightReport: async function (
    { studyId, reportName },
    cancel = false
  ) {
    try {
      const presignedUrlResponse = await api.request({
        url: `/studies/${studyId}/reports/${encodeURIComponent(reportName)}`,
        method: "GET",
        signal: cancel
          ? cancelApiObject[
              this.downloadTrafficLightReport.name
            ].handleRequestCancellation().signal
          : undefined,
      });

      const { url } = presignedUrlResponse.data;

      if (isProduction) {
        Sentry.setContext("trafficLightReportDownload", {
          url: url,
          studyId: studyId,
          reportName: reportName,
          timestamp: new Date().toISOString(),
        });
      }

      try {
        const response = await s3Api.get(url, {
          responseType: "blob",
          signal: cancel
            ? cancelApiObject[
                this.downloadTrafficLightReport.name
              ].handleRequestCancellation().signal
            : undefined,
        });

        if (isProduction) {
          Sentry.setContext("trafficLightReportDownloadResponse", {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(Object.entries(response.headers)),
            size: response.data.size,
            type: response.data.type,
          });
        }

        return response.data;
      } catch (presignedError) {
        const error = new Error(
          "Traffic light report presigned URL request failed"
        );
        error.originalError = presignedError;
        error.isPresignedUrlError = true;
        error.status = presignedError.response?.status;
        if (isProduction) {
          Sentry.captureException(error);
        }
        throw error;
      }
    } catch (error) {
      if (isProduction) {
        Sentry.captureException(error);
      }
      if (!error.isPresignedUrlError) {
        throw error;
      }
      const newError = new Error("Failed to download traffic light report");
      newError.originalError = error;
      throw newError;
    }
  },
  deleteAllTfls: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/tfls`,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[this.deleteAllTfls.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  reloadTfls: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/tfls/reload`,
      method: "POST",
      signal: cancel
        ? cancelApiObject[this.reloadTfls.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    return response.data;
  },
  filterTables: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: `/studies/${studyId}/filter`,
      method: "POST",
      data: {},
      signal: cancel
        ? cancelApiObject[this.filterTables.name].handleRequestCancellation()
            .signal
        : undefined,
    });

    return response.data;
  },
  getAdvSearchDashboard: async function ({ studyId }, cancel = false) {
    console.log(studyId);
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/dashboard",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getAdvSearchDashboard.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  getTableConfiguration: async function ({ studyId, tableId }, cancel = false) {
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/tables/" + tableId + "/queries",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getTableConfiguration.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  putTableGeneration: async function (
    { studyId, tableId, body },
    cancel = false
  ) {
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/tables/" + tableId + "/generation",
      method: "POST",
      data: body,
      signal: cancel
        ? cancelApiObject[
            this.putTableGeneration.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  getTableRequestStatus: async function ({ studyId, tableId }, cancel = false) {
    const response = await api.request({
      url:
        "/advanced_search/" +
        studyId +
        "/tables/" +
        tableId +
        "/request_status",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getTableRequestStatus.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  getTableOutput: async function ({ studyId, tableId }, cancel = false) {
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/tables/" + tableId + "/output",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getTableOutput.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  putEditedResponse: async function (
    { studyId, tableId, editedContent },
    cancel = false
  ) {
    const response = await api.request({
      url: "/advanced_search/" + studyId + "/tables/" + tableId + "/output",
      method: "POST",
      data: { edited_output: editedContent },
      signal: cancel
        ? cancelApiObject[
            this.putEditedResponse.name
          ]?.handleRequestCancellation?.()?.signal
        : undefined,
    });
    return response.data;
  },
  //Lay Summary specific APIs
  getAllStudiesForLaySummaries: async function (cancel = false) {
    const response = await api.request({
      url: "/studies?status=RENDER_AVAILABLE",
      method: "GET",
      signal: cancel
        ? cancelApiObject[this.getAllStudies.name].handleRequestCancellation()
            .signal
        : undefined,
    });
    const studies = response.data.studies.map(obj => Object.values(obj)).flat();
    return studies;
  },
  getAllLaySummaries: async function (cancel = false) {
    const response = await api.request({
      url: "/lay-summaries",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getAllLaySummaries.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    const studies = response.data.studies.map(obj => Object.values(obj)).flat();
    return studies;
  },
  createLaySummary: async function (
    { laySummaryName, selectedStudyIDValue, selectedStudy },
    cancel = false
  ) {
    const response = await api.request({
      url: "/lay-summaries",
      method: "POST",
      data: {
        lay_summary_name: laySummaryName,
        CSR_study_id: Number(selectedStudyIDValue),
        CSR_study_type: "INTERNAL_CSR",
        CSR_study_name: selectedStudy,
      },
      signal: cancel
        ? cancelApiObject[
            this.createLaySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response.data;
  },
  deleteLaySummary: async function (lay_summary_id, cancel = false) {
    const response = await api.request({
      url: "/lay-summaries/" + lay_summary_id,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[
            this.deleteLaySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response;
  },
  deleteSafetySummary: async function (summaryId, cancel = false) {
    const response = await api.request({
      url: "/clinical_summaries/safety_summary/" + summaryId,
      method: "DELETE",
      signal: cancel
        ? cancelApiObject[
            this.deleteSafetySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });
    return response;
  },
  getLaySummaryQuestions: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/lay-summaries/" + studyId + "/wizards",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getLaySummaryQuestions.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  saveLaySummaryQuestions: async function ({ studyId, state }, cancel = false) {
    const response = await api.request({
      url: "/lay-summaries/" + studyId + "/wizards",
      method: "PUT",
      data: state,
      signal: cancel
        ? cancelApiObject[
            this.saveLaySummaryQuestions.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  getLatestLaySummaryRequest: async function (
    { lay_summary_id },
    cancel = false
  ) {
    const response = await api.request({
      url: "/lay-summaries/" + lay_summary_id + "/requests-latest",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getLatestLaySummaryRequest.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  generateLaySummary: async function ({ studyId }, cancel = false) {
    const response = await api.request({
      url: "/lay-summaries/" + studyId,
      method: "POST",
      signal: cancel
        ? cancelApiObject[
            this.generateLaySummary.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  getLaySummaryStatus: async function (
    { lay_summary_id, requestId },
    cancel = false
  ) {
    const response = await api.request({
      url:
        "/lay-summaries/" +
        lay_summary_id +
        "/requests/" +
        requestId +
        "/status",
      method: "GET",
      signal: cancel
        ? cancelApiObject[
            this.getLaySummaryStatus.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
  abortLaySummaryGeneration: async function (
    { studyId, requestId },
    cancel = false
  ) {
    const response = await api.request({
      url: "/lay-summaries/" + studyId + "/lay-summaries/" + requestId,
      method: "POST",
      signal: cancel
        ? cancelApiObject[
            this.abortLaySummaryGeneration.name
          ].handleRequestCancellation().signal
        : undefined,
    });

    return response.data;
  },
};

// defining the cancel API object for CsrAPI
const cancelApiObject = defineCancelApiObject(CsrAPI);
